import React, { useState } from "react";
import { ArrowRight, Check, BookOpen, GraduationCap, PhoneCall } from "lucide-react";
import CurriculumDialog from "./CurriculumDialog";
import { track } from "../../lib/analytics";

const tiers = [
  {
    id: "pro",
    code: "T1 / PRO",
    overline: "The Career Shift",
    name: "Master Hunter Certification",
    cadence: "4-Week Live Cohort · By Application",
    target: "Mid-level managers · Complex B2B & institutional negotiation",
    features: [
      "Live cohort with founder Mandar",
      "Institutional procurement frameworks",
      "Long-cycle deal architecture",
      "Live deal reviews & role-play",
      "Master Hunter certification",
      "Private alumni network",
    ],
    highlight: true,
    cta: "Apply for the Next Cohort",
  },
  {
    id: "elite",
    code: "T2 / ELITE",
    overline: "The Exclusivity Tier",
    name: "Executive 1-on-1 Mentorship",
    cadence: "Personalised · Limited Seats · By Invitation",
    target: "Future CEOs · High-performing founders & VPs",
    features: [
      "Direct 1-on-1 mentorship cycle",
      "Custom revenue architecture review",
      "Strategic account war-gaming",
      "Hiring & sales-team blueprint",
      "Quarterly executive scorecard",
      "Priority access to proprietary tech",
    ],
    highlight: false,
    cta: "Request Consideration",
  },
];

const COURSE_INTEREST_MAP = {
  "frontline-elite": "Frontline Elite (Retail)",
  "rookie-hunter": "Rookie Hunter (B2B)",
  pro: "Master Hunter Pro",
  elite: "Elite 1-on-1 Mentorship",
};

function fireInterestPrefill(courseKey) {
  const interest = COURSE_INTEREST_MAP[courseKey];
  if (!interest) return;
  window.dispatchEvent(
    new CustomEvent("sebyx-prefill", { detail: { interest } })
  );
}

const ENTRY_COURSES = [
  {
    id: "frontline-elite",
    code: "ENTRY · FRONTLINE ELITE",
    name: "Frontline Elite",
    icon: GraduationCap,
    target:
      "Ambitious young salespeople (18-28) in India's premium and high-end retail — first or second job.",
    highlights: [
      "8 modules · 60+ real scripts for the sales floor",
      "60% role-play, 40% frameworks",
      "Sebyx Frontline Elite Certification + Playbook",
    ],
    curriculumUrl: "/frontline-curriculum",
  },
  {
    id: "rookie-hunter",
    code: "ENTRY · ROOKIE HUNTER",
    name: "Rookie Hunter",
    icon: PhoneCall,
    target:
      "B2B freshers (0-2 yrs) — SDRs, BDRs, inside-sales, new field-sales hires across SaaS, industrial, services.",
    highlights: [
      "8 modules · 60+ scripts — email, phone, LinkedIn",
      "Cold outreach, discovery, objection handling, first close",
      "Sebyx Rookie Hunter Certification + Playbook",
    ],
    curriculumUrl: "/rookie-hunter",
  },
];

export default function Academy() {
  const [curriculumTier, setCurriculumTier] = useState(null);
  return (
    <section
      id="academy"
      data-testid="academy-section"
      className="relative py-24 lg:py-32 px-6 md:px-12 lg:px-24 border-t border-sebyx-border"
    >
      {/* Header */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
        <div className="lg:col-span-4">
          <div className="flex items-center gap-3 mb-6">
            <span className="block w-8 h-px bg-sebyx-accent" />
            <span
              data-testid="academy-eyebrow"
              className="font-mono text-[11px] tracking-[0.3em] uppercase text-sebyx-accent"
            >
              [07] Sebyx Academy
            </span>
          </div>
          <h2
            data-testid="academy-headline"
            className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-sebyx-text leading-[1.05]"
          >
            A Value Ladder for{" "}
            <span className="text-sebyx-accent">every stage</span> of your sales
            career.
          </h2>
        </div>
        <div className="lg:col-span-7 lg:col-start-6 lg:pt-3">
          <p className="font-body text-base md:text-lg leading-relaxed text-sebyx-muted">
            Three tiers. One philosophy: revenue is engineered, not accidental.
            From the junior rep building their first pipeline to the CEO
            architecting an enterprise revenue engine — Sebyx Academy meets you
            on the ground and walks you to the boardroom.
          </p>
        </div>
      </div>

      {/* Entry Level courses */}
      <div className="mb-16">
        <div className="flex items-center gap-3 mb-8">
          <span className="block w-8 h-px bg-sebyx-accent" />
          <span
            data-testid="academy-entry-eyebrow"
            className="font-mono text-[11px] tracking-[0.3em] uppercase text-sebyx-accent"
          >
            Entry Level · Start Your Sales Career
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 border border-sebyx-border bg-sebyx-border gap-px">
          {ENTRY_COURSES.map((c) => {
            const Icon = c.icon;
            return (
              <article
                key={c.id}
                data-testid={`academy-entry-${c.id}`}
                className="relative bg-sebyx-surface p-8 lg:p-10 flex flex-col group hover:bg-sebyx-elevated transition-colors duration-300"
              >
                <div className="flex items-center justify-between mb-5">
                  <span className="font-mono text-[11px] tracking-[0.25em] uppercase text-sebyx-accent">
                    {c.code}
                  </span>
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-sebyx-muted">
                    2 Days · 12 Hours
                  </span>
                </div>
                <div className="flex items-start gap-4 mb-3">
                  <div className="w-11 h-11 border border-sebyx-accent flex items-center justify-center shrink-0">
                    <Icon size={18} className="text-sebyx-accent" />
                  </div>
                  <h3 className="font-display text-2xl lg:text-3xl font-semibold tracking-tight text-sebyx-text leading-tight">
                    {c.name}
                  </h3>
                </div>
                <p className="font-body text-sm text-sebyx-muted mb-6 leading-relaxed">
                  {c.target}
                </p>
                <div className="border-y border-sebyx-border py-5 mb-6">
                  <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                      <span className="block w-1.5 h-1.5 rotate-45 bg-sebyx-accent" />
                      <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-sebyx-accent">
                        Invitation-only
                      </span>
                    </div>
                    <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-sebyx-muted">
                      10–12 seats · Investment shared on call
                    </span>
                  </div>
                </div>
                <ul className="space-y-2 mb-8 flex-1">
                  {c.highlights.map((h, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-3 text-sm text-sebyx-text/90"
                    >
                      <Check
                        size={14}
                        strokeWidth={2.5}
                        className="text-sebyx-accent shrink-0 mt-1"
                      />
                      <span className="leading-relaxed">{h}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href={c.curriculumUrl}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`academy-entry-${c.id}-curriculum`}
                    className="inline-flex items-center justify-center gap-2 px-4 py-3 border border-sebyx-accent text-sebyx-accent font-semibold text-sm hover:bg-sebyx-accent/10 transition-colors flex-1"
                  >
                    <BookOpen size={14} /> View Curriculum
                  </a>
                  <a
                    href="#contact"
                    data-testid={`academy-entry-${c.id}-cta`}
                    onClick={() => {
                      track("academy_cta_clicked", {
                        tier_id: c.id,
                        action: "enquire",
                      });
                      fireInterestPrefill(c.id);
                    }}
                    className="inline-flex items-center justify-center gap-2 px-4 py-3 bg-sebyx-accent text-sebyx-bg font-semibold text-sm hover:bg-[#3FA097] transition-colors flex-1"
                  >
                    Apply for the Next Cohort <ArrowRight size={14} />
                  </a>
                </div>
              </article>
            );
          })}
        </div>
      </div>

      {/* Career Level — Pro + Elite */}
      <div className="flex items-center gap-3 mb-8">
        <span className="block w-8 h-px bg-sebyx-accent" />
        <span
          data-testid="academy-career-eyebrow"
          className="font-mono text-[11px] tracking-[0.3em] uppercase text-sebyx-accent"
        >
          Career Level · Advance Your Craft
        </span>
      </div>

      {/* Pricing grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 border border-sebyx-border bg-sebyx-border gap-px max-w-5xl mx-auto">
        {tiers.map((t) => (
          <article
            key={t.id}
            data-testid={`academy-tier-${t.id}`}
            className={`relative bg-sebyx-surface p-8 lg:p-10 flex flex-col group hover:bg-sebyx-elevated transition-colors duration-300 ${
              t.highlight ? "lg:scale-[1.02] lg:-my-2 z-10 ring-1 ring-sebyx-accent" : ""
            }`}
          >
            {t.highlight && (
              <div
                data-testid={`academy-tier-${t.id}-badge`}
                className="absolute -top-3 left-8 px-3 py-1 bg-sebyx-accent text-sebyx-bg font-mono text-[10px] tracking-[0.2em] uppercase"
              >
                Most Chosen
              </div>
            )}
            {/* Code line */}
            <div className="flex items-center justify-between mb-5">
              <span className="font-mono text-[11px] tracking-[0.25em] uppercase text-sebyx-accent">
                {t.code}
              </span>
              <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-sebyx-muted">
                {t.overline}
              </span>
            </div>

            <h3 className="font-display text-2xl lg:text-3xl font-semibold tracking-tight text-sebyx-text mb-2">
              {t.name}
            </h3>
            <p className="font-body text-sm text-sebyx-muted mb-8">
              {t.target}
            </p>

            {/* Investment / Access */}
            <div className="border-y border-sebyx-border py-6 mb-8">
              <div className="flex items-center gap-2">
                <span className="block w-1.5 h-1.5 rotate-45 bg-sebyx-accent" />
                <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-sebyx-accent">
                  Invitation-only
                </span>
              </div>
              <div className="font-display text-xl lg:text-2xl font-semibold text-sebyx-text tracking-tight mt-2">
                Programme Investment
                <span className="text-sebyx-muted"> shared on qualification call</span>
              </div>
              <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-sebyx-muted block mt-2">
                {t.cadence}
              </span>
              {(t.id === "pro" || t.id === "elite") && (
                <button
                  type="button"
                  data-testid={`academy-tier-${t.id}-view-curriculum`}
                  onClick={() => {
                    track("curriculum_viewed", { tier_id: t.id });
                    setCurriculumTier(t.id);
                  }}
                  className="mt-5 inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase text-sebyx-accent border-b border-sebyx-accent/60 hover:border-sebyx-accent hover:gap-3 pb-1 transition-all"
                >
                  <BookOpen size={12} strokeWidth={2} />
                  View Full Curriculum
                </button>
              )}
            </div>

            {/* Features */}
            <ul className="space-y-3 mb-10 flex-1">
              {t.features.map((f, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 text-sm text-sebyx-text/90"
                >
                  <Check
                    size={14}
                    strokeWidth={2.5}
                    className="text-sebyx-accent shrink-0 mt-1"
                  />
                  <span className="leading-relaxed">{f}</span>
                </li>
              ))}
            </ul>

            {t.id === "elite" ? (
              <a
                href="#contact"
                data-testid={`academy-tier-${t.id}-cta`}
                onClick={() => {
                  track("academy_cta_clicked", { tier_id: t.id, action: "apply" });
                  fireInterestPrefill(t.id);
                }}
                className={`inline-flex items-center justify-between gap-2 px-5 py-4 font-semibold text-sm transition-colors ${
                  t.highlight
                    ? "bg-sebyx-accent text-sebyx-bg hover:bg-[#3FA097]"
                    : "border border-sebyx-accent text-sebyx-accent hover:bg-sebyx-accent/10"
                }`}
              >
                <span>{t.cta}</span>
                <ArrowRight size={16} />
              </a>
            ) : (
              <a
                href="#contact"
                data-testid={`academy-tier-${t.id}-cta`}
                onClick={() => {
                  track("academy_cta_clicked", { tier_id: t.id, action: "enquire" });
                  fireInterestPrefill(t.id);
                }}
                className={`inline-flex items-center justify-between gap-2 px-5 py-4 font-semibold text-sm transition-colors ${
                  t.highlight
                    ? "bg-sebyx-accent text-sebyx-bg hover:bg-[#3FA097]"
                    : "border border-sebyx-accent text-sebyx-accent hover:bg-sebyx-accent/10"
                }`}
              >
                <span>{t.cta}</span>
                <ArrowRight size={16} />
              </a>
            )}
          </article>
        ))}
      </div>

      <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-sebyx-muted mt-8">
        * Enrolment confirmed after a short qualifying call · Elite tier is by application only.
      </p>

      <CurriculumDialog
        tierId={curriculumTier}
        open={Boolean(curriculumTier)}
        onOpenChange={(next) => {
          if (!next) setCurriculumTier(null);
        }}
      />
    </section>
  );
}
